export type Tournament = {
  id: string;
  creatorId?: string;
  name: string;
  description: string;
  startAt: string;
  endAt: string;
  country: string;
  region: string;
  visibility: string;
  status: string;
  entryFeeCents: number;
  currency: string;
  paymentRequired: boolean;
  joinApprovalRequired: boolean;
  paymentMethod: string;
  paymentInstructions: string;
  bankCountry: string;
  bankAccountHolder: string;
  bankName: string;
  iban: string;
  swiftBic: string;
  accountNumber: string;
  routingNumber: string;
  sortCode: string;
  branchCode: string;
  bankAddress: string;
  recipientAddress: string;
  otherPaymentDetails: string;
  scoringMethod: string;
  scoringMetric: "LENGTH" | "WEIGHT" | "LENGTH_AND_WEIGHT";
  rules: string;
  participantLimit: number;
  registrationCount: number;
  allowAnySpecies: boolean;
  photoRequired: boolean;
  videoRequired: boolean;
  mediaSourceRule: "ALLOW_UPLOAD_OR_CAMERA" | "CAMERA_ONLY";
  gpsRequired: boolean;
  imageUrl: string | null;
  species: string[];
  tags: string[];
};

export type RegistrationItem = {
  id: string;
  tournamentId: string;
  tournamentName: string;
  name: string;
  email: string;
  phone: string;
  teamName: string;
  paymentStatus: string;
  status: string;
  createdAt: string;
};

export type AuthUser = {
  id: string;
  email: string;
  role: "ANGLER" | "ORGANIZER" | "ADMIN";
  displayName?: string | null;
  subscriptionTier?: "FREE" | "PRO" | "PRO_ARCHIVE";
};

export type NotificationItem = {
  id: string;
  createdAt: string;
  kind: string;
  title: string;
  body: string | null;
  readAt: string | null;
  tournamentId?: string;
  tournamentName?: string;
};

export type SubmissionItem = {
  id: string;
  tournamentId: string;
  tournamentName: string;
  participantName: string;
  participantEmail: string;
  species: string;
  length: number;
  weight: number;
  photoUrl: string;
  videoUrl: string;
  notes: string;
  lat: number;
  lng: number;
  gpsAccuracy: number;
  gpsCapturedAt: string;
  mediaCaptureMode?: string;
  reviewNotes?: string;
  status: string;
  createdAt: string;
};

export type AnnouncementItem = {
  id: string;
  tournamentId: string;
  tournamentName: string;
  creatorId: string;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  readAt: string | null;
};

export type DashboardCounts = {
  activeTournaments: number;
  liveEvents: number;
  upcomingEvents: number;
  pendingCatchSubmissions: number;
  joinRequests: number;
  announcements: number;
  unreadAnnouncements: number;
  nextLiveEndsAt: string;
};

export type TournamentRankingItem = {
  rank: number;
  participantName: string;
  participantEmail: string;
  catches: number;
  bestLength: number;
  bestWeight: number;
  score: number;
};

export type TournamentDashboardData = {
  tournament: Tournament;
  participants: RegistrationItem[];
  rankings: TournamentRankingItem[];
  topCatches: SubmissionItem[];
  topBySpecies: SubmissionItem[];
};

export type CreateTournamentPayload = {
  name: string;
  description: string;
  imageUrl: string;
  startAt: string;
  endAt: string;
  country: string;
  region: string;
  visibility: string;
  status: string;
  scoringMethod: string;
  rules: string;
  participantLimit: number;
  species: string;
  tags: string;
  entryFeeCents: number;
  currency: string;
  paymentInstructions: string;
  bankCountry: string;
  bankAccountHolder: string;
  bankName: string;
  iban: string;
  swiftBic: string;
  accountNumber: string;
  routingNumber: string;
  sortCode: string;
  branchCode: string;
  bankAddress: string;
  recipientAddress: string;
  otherPaymentDetails: string;
  paymentRequired: boolean;
  joinApprovalRequired: boolean;
  paymentMethod: string;
  scoringMetric: string;
  allowAnySpecies: boolean;
  photoRequired: boolean;
  videoRequired: boolean;
  mediaSourceRule: string;
  gpsRequired: boolean;
};

type TournamentResponse = {
  items?: Tournament[];
  tournaments?: Tournament[];
};

const DEFAULT_API = "https://reelmaster.area.lv/index.php?rest_route=/reelmaster/v1";
const DEFAULT_APP_API = DEFAULT_API;
const TOKEN_KEY = "token";
const USER_KEY = "user";
let memoryToken: string | null = null;
let memoryUser: AuthUser | null = null;

function safeLocalStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function apiBaseUrl() {
  return (import.meta.env.VITE_REELMASTER_API_URL || DEFAULT_API).replace(/\/$/, "");
}

export function appApiBaseUrl() {
  return (import.meta.env.VITE_REELMASTER_APP_API_URL || DEFAULT_APP_API).replace(/\/$/, "");
}

export function buildApiUrl(path: string, query: Record<string, string> = {}) {
  const base = apiBaseUrl();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  if (base.includes("rest_route=")) {
    const url = new URL(base);
    const route = url.searchParams.get("rest_route") || "";
    url.searchParams.set("rest_route", `${route.replace(/\/$/, "")}${cleanPath}`);
    Object.entries(query).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
    return url.toString();
  }

  const url = new URL(`${base}${cleanPath}`);
  Object.entries(query).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });
  return url.toString();
}

export function buildAppApiUrl(path: string, query: Record<string, string> = {}) {
  const base = appApiBaseUrl();
  const [rawPath, rawQuery = ""] = path.split("?");
  const cleanPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
  const mergedQuery = { ...Object.fromEntries(new URLSearchParams(rawQuery)), ...query };

  if (base.includes("rest_route=")) {
    const url = new URL(base);
    const route = url.searchParams.get("rest_route") || "";
    url.searchParams.set("rest_route", `${route.replace(/\/$/, "")}${cleanPath}`);
    Object.entries(mergedQuery).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
    return url.toString();
  }

  const url = new URL(`${base}${cleanPath}`);
  Object.entries(mergedQuery).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });
  return url.toString();
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(buildApiUrl(path), {
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      json && typeof json === "object" && "message" in json
        ? String((json as { message: unknown }).message)
        : `Request failed (${res.status})`;
    throw new Error(message);
  }
  return json as T;
}

function parseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function getToken() {
  try {
    return safeLocalStorage()?.getItem(TOKEN_KEY) || memoryToken;
  } catch {
    return memoryToken;
  }
}

export function getCurrentUser(): AuthUser | null {
  let raw: string | null = null;
  try {
    raw = safeLocalStorage()?.getItem(USER_KEY) || null;
  } catch {
    raw = null;
  }
  if (!raw) return null;
  try {
    memoryUser = JSON.parse(raw) as AuthUser;
    return memoryUser;
  } catch {
    return memoryUser;
  }
}

export function setSession(session: { token: string; user: AuthUser }) {
  memoryToken = session.token;
  memoryUser = session.user;
  try {
    const storage = safeLocalStorage();
    storage?.setItem(TOKEN_KEY, session.token);
    storage?.setItem(USER_KEY, JSON.stringify(session.user));
  } catch {
    // Some mobile privacy modes block storage; keep the in-memory session active.
  }
}

export function clearSession() {
  memoryToken = null;
  memoryUser = null;
  try {
    const storage = safeLocalStorage();
    storage?.removeItem(TOKEN_KEY);
    storage?.removeItem(USER_KEY);
  } catch {
    // Ignore storage cleanup failures from restricted mobile browsers.
  }
}

function appEndpoint(path: string) {
  return `/${path.split("?")[0].replace(/^\/+/, "")}`;
}

function isAuthEndpoint(path: string) {
  const endpoint = appEndpoint(path);
  return endpoint === "/auth/login" || endpoint === "/auth/register";
}

function needsAppToken(path: string, init?: RequestInit) {
  if (isAuthEndpoint(path)) return false;
  const method = (init?.method || "GET").toUpperCase();
  if (method !== "GET") return true;
  const endpoint = appEndpoint(path);
  return ["/dashboard", "/notifications", "/profile", "/registrations", "/submissions"].some((prefix) => endpoint.startsWith(prefix));
}

export async function appApiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const shouldAttachToken = Boolean(token && !isAuthEndpoint(path));
  if (!token && needsAppToken(path, init)) {
    clearSession();
    throw new Error("Your session expired. Please log in again.");
  }
  const formBody = init?.body instanceof FormData ? init.body : null;
  let body = init?.body ?? null;
  let headers: HeadersInit = init?.headers ?? {};

  const method = (init?.method || "GET").toUpperCase();

  if (init?.body && typeof init.body === "string" && method === "POST") {
    const parsed = parseJson(init.body);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const form = new URLSearchParams();
      Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
        if (value !== undefined && value !== null) form.append(key, String(value));
      });
      body = form;
    }
  }

  if (formBody && shouldAttachToken && token) {
    formBody.set("token", token);
  } else if (body instanceof FormData && shouldAttachToken && token) {
    body.set("token", token);
  } else if (body && !(body instanceof FormData) && !(body instanceof URLSearchParams)) {
    headers = {
      "content-type": "application/json",
      ...headers,
    };
  }

  let res: Response;
  try {
    res = await fetch(buildAppApiUrl(path, shouldAttachToken && token ? { token } : {}), {
      ...init,
      body,
      headers,
    });
  } catch {
    throw new Error(`Could not reach the Reelmaster app API at ${appApiBaseUrl()}. Check that the API domain exists and points to the running backend.`);
  }
  const text = await res.text();
  const json = parseJson(text);
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    if (json && typeof json === "object" && "error" in json) {
      message = String((json as { error: unknown }).error);
    } else if (json && typeof json === "object" && "message" in json) {
      message = String((json as { message: unknown }).message);
    } else if (text && text.length < 200) {
      message = text;
    }
    if (res.status === 401 && /token|session|unauthori[sz]ed/i.test(message)) {
      clearSession();
      message = "Your session expired. Please log in again.";
    }
    throw new Error(message);
  }
  return json as T;
}

export async function login(identifier: string, password: string) {
  return appApiFetch<{ token: string; user: AuthUser }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email: identifier, password }),
  });
}

export async function registerAccount(payload: { email: string; password: string; displayName: string }) {
  return appApiFetch<{ token: string; user: AuthUser }>("/auth/register", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listNotifications() {
  return appApiFetch<{ items: NotificationItem[] }>("/notifications?page=1&pageSize=50");
}

export async function getDashboardCounts() {
  return appApiFetch<DashboardCounts>("/dashboard/counts");
}

export async function listAnnouncements(tournamentId = "") {
  const query = tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}` : "";
  return appApiFetch<{ items: AnnouncementItem[] }>(`/announcements${query}`);
}

export async function createAnnouncement(payload: { tournamentId: string; title: string; content: string }) {
  return appApiFetch<{ announcement: AnnouncementItem }>("/announcements", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAnnouncement(id: string, payload: { title: string; content: string }) {
  return appApiFetch<{ announcement: AnnouncementItem }>(`/announcements/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteAnnouncement(id: string) {
  return appApiFetch<{ ok: boolean }>(`/announcements/${id}`, {
    method: "DELETE",
  });
}

export async function markAnnouncementRead(id: string) {
  return appApiFetch<{ announcement: AnnouncementItem }>(`/announcements/${id}/read`, {
    method: "PATCH",
  });
}

export async function createTournament(payload: CreateTournamentPayload) {
  return appApiFetch<{ tournament: Tournament }>("/tournaments", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateTournament(id: string, payload: CreateTournamentPayload) {
  return appApiFetch<{ tournament: Tournament }>(`/tournaments/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteTournament(id: string) {
  return appApiFetch<{ ok: boolean }>(`/tournaments/${id}`, {
    method: "DELETE",
  });
}

export async function updateProfile(payload: { displayName: string; phone?: string; teamName?: string; subscriptionTier?: AuthUser["subscriptionTier"] }) {
  return appApiFetch<{ user: AuthUser }>("/profile", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function updatePassword(payload: { currentPassword: string; newPassword: string }) {
  return appApiFetch<{ ok: boolean; user: AuthUser }>("/profile/password", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function uploadMedia(file: File, captureMode: string) {
  const form = new FormData();
  form.set("file", file);
  form.set("captureMode", captureMode);
  return appApiFetch<{ url: string; attachmentId: number; captureMode: string }>("/media", {
    method: "POST",
    body: form,
  });
}

export async function getTournamentDashboard(id: string) {
  return apiFetch<TournamentDashboardData>(`/tournaments/${id}/dashboard`);
}

export async function listRegistrations(status = "") {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  return appApiFetch<{ items: RegistrationItem[] }>(`/registrations${query}`);
}

export async function reviewRegistration(id: string, payload: { status: string; paymentStatus?: string }) {
  const query = new URLSearchParams({ status: payload.status });
  if (payload.paymentStatus) query.set("paymentStatus", payload.paymentStatus);
  return appApiFetch<{ registration: RegistrationItem }>(`/registrations/${id}/review?${query.toString()}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function reviewSubmission(id: string, payload: { status: string; reviewNotes?: string }) {
  const query = new URLSearchParams({ status: payload.status });
  if (payload.reviewNotes) query.set("reviewNotes", payload.reviewNotes);
  return appApiFetch<{ submission: SubmissionItem }>(`/submissions/${id}/review?${query.toString()}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateSubmission(id: string, payload: Partial<SubmissionItem>) {
  return appApiFetch<{ submission: SubmissionItem }>(`/submissions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function withdrawSubmission(id: string) {
  return appApiFetch<{ submission: SubmissionItem }>(`/submissions/${id}`, {
    method: "DELETE",
  });
}

export async function listSubmissions(status = "") {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  return appApiFetch<{ items: SubmissionItem[] }>(`/submissions${query}`);
}

export async function listTournaments(filters: Record<string, string>) {
  const res = await fetch(buildApiUrl("/tournaments", filters));
  if (!res.ok) throw new Error(`Could not load tournaments (${res.status})`);
  const data = (await res.json()) as TournamentResponse;
  return data.items ?? data.tournaments ?? [];
}

export function stripHtml(html: string) {
  const el = document.createElement("div");
  el.innerHTML = html;
  return el.textContent || el.innerText || "";
}
